import { lazy, Suspense } from "react";
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useParams,
} from "react-router-dom";

import ErrorBoundary from "./components/ErrorBoundary";
import { OrgTypeGate } from "./components/OrgTypeGate";
import { batteryMfgPaths, cellMfgPaths } from "./features/Manufacturing/paths";
import { AuthProvider } from "./hooks/useAuth";
import { DashboardLayout } from "./layouts/DashboardLayout";
import { LegacyRedirect } from "./pages/Manufacturing/LegacyRedirect";
import {
  ALL_MFR_TYPES,
  CELL_MFR_TYPES,
  EPR_ELIGIBLE_TYPES,
  WRITE_CAPABLE_TYPES,
} from "./types/orgTypes";

// Lazy load from pages
const AuthLoginRoute = lazy(() =>
  import("./pages/Login").then((module) => ({
    default: module.AuthLoginRoute,
  }))
);
const AcceptInvitePage = lazy(() =>
  import("./pages/AcceptInvite").then((module) => ({
    default: module.AcceptInvitePage,
  }))
);
const DigitalIdentityDetailsPage = lazy(() =>
  import("@/features/DigitalIdentity/_slug/view/index").then((module) => ({
    default: module.DigitalIdentityDetailRoute,
  }))
);
const PublicBatteryPassportPage = lazy(() =>
  import("@/features/DigitalIdentity/publicView").then((module) => ({
    default: module.PublicBatteryPassportRoute,
  }))
);
const TraceRedirect = () => {
  const { passportId } = useParams<{ passportId: string }>();
  return <Navigate replace to={`/digital-identity/trace/${passportId}`} />;
};
const DashboardPage = lazy(() => import("./pages/Dashboard"));
const DigitalIdentityPage = lazy(() => import("./pages/DigitalIdentity"));
const BatteryModelsPage = lazy(() => import("./pages/BatteryModels"));
const MaterialsPage = lazy(() => import("./pages/Materials"));
const PartnersPage = lazy(() => import("./pages/Partners"));
const AccessControlPage = lazy(() => import("./pages/AccessControl"));
const InboxPage = lazy(() => import("./pages/Inbox"));
const AccessRequestsPage = lazy(() => import("./pages/AccessRequests"));
const OrganizationPage = lazy(() => import("./pages/organization"));
const EPRPage = lazy(() => import("./pages/EPR"));
const IconPage = lazy(() => import("./pages/IconPage"));
const CellModelsPage = lazy(() => import("./pages/CellModels"));
const ManufacturingPage = lazy(() => import("./pages/Manufacturing"));
const WarrantyPage = lazy(() => import("./pages/Warranty"));
// const RecallPage = lazy(() => import("./pages/Recall"));
// const ComingSoonPage = lazy(() => import("./pages/ComingSoon"));

const RouteLoading = () => (
  <div className="flex h-48 items-center justify-center p-8">
    <p className="animate-pulse text-muted-foreground">Loading...</p>
  </div>
);

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ErrorBoundary>
          <Suspense fallback={<RouteLoading />}>
            <Routes>
              <Route element={<AuthLoginRoute />} path="/auth/login" />
              <Route
                element={<AcceptInvitePage />}
                path="/auth/accept-invite"
              />
              <Route element={<DigitalIdentityDetailsPage />} path="/di/:id" />
              <Route
                element={<PublicBatteryPassportPage />}
                path="/di/:id/public"
              />
              <Route element={<TraceRedirect />} path="/trace/:passportId" />

              <Route element={<DashboardLayout />}>
                <Route element={<Navigate replace to="/overview" />} path="/" />
                <Route element={<DashboardPage />} path="/overview/*" />
                <Route
                  element={<DigitalIdentityPage />}
                  path="/digital-identity/*"
                />
                <Route
                  element={
                    <OrgTypeGate orgTypes={ALL_MFR_TYPES}>
                      <BatteryModelsPage />
                    </OrgTypeGate>
                  }
                  path="/battery-models/*"
                />
                <Route
                  element={
                    <OrgTypeGate orgTypes={ALL_MFR_TYPES}>
                      <MaterialsPage />
                    </OrgTypeGate>
                  }
                  path="/inventory/*"
                />
                <Route
                  element={
                    <OrgTypeGate orgTypes={WRITE_CAPABLE_TYPES}>
                      <PartnersPage />
                    </OrgTypeGate>
                  }
                  path="/partners/*"
                />
                <Route
                  element={<AccessControlPage />}
                  path="/access-control/*"
                />
                <Route element={<InboxPage />} path="/inbox/*" />
                <Route
                  element={<AccessRequestsPage />}
                  path="/access-requests/*"
                />
                <Route element={<OrganizationPage />} path="/organization/*" />
                <Route
                  element={<ManufacturingPage />}
                  path="/manufacturing/*"
                />
                <Route
                  element={<LegacyRedirect to={cellMfgPaths.base()} />}
                  path="/cell-manufacturing/*"
                />
                <Route
                  element={<LegacyRedirect to={batteryMfgPaths.base()} />}
                  path="/battery-manufacturing/*"
                />
                <Route
                  element={
                    <OrgTypeGate orgTypes={EPR_ELIGIBLE_TYPES}>
                      <EPRPage />
                    </OrgTypeGate>
                  }
                  path="/epr/*"
                />
                <Route
                  element={
                    <OrgTypeGate orgTypes={CELL_MFR_TYPES}>
                      <CellModelsPage />
                    </OrgTypeGate>
                  }
                  path="/cell-models/*"
                />
                <Route
                  element={
                    <OrgTypeGate orgTypes={ALL_MFR_TYPES}>
                      <WarrantyPage />
                    </OrgTypeGate>
                  }
                  path="/warranty/*"
                />
                {/* <Route
                  element={
                    <OrgTypeGate orgTypes={ALL_MFR_TYPES}>
                      <RecallPage />
                    </OrgTypeGate>
                  }
                  path="/recall/*"
                /> */}

                {/* Phase 2 — stub routes (gated, show coming soon) */}
                {/*<Route
                  element={
                    <OrgTypeGate orgTypes={INTEGRATION_TYPES}>
                      <ComingSoonPage />
                    </OrgTypeGate>
                  }
                  path="/vehicle-binding/*"
                />
                <Route
                  element={
                    <OrgTypeGate orgTypes={["service_provider"]}>
                      <ComingSoonPage />
                    </OrgTypeGate>
                  }
                  path="/service-events/*"
                />
                <Route
                  element={
                    <OrgTypeGate orgTypes={["fleet_operator"]}>
                      <ComingSoonPage />
                    </OrgTypeGate>
                  }
                  path="/fleet-telemetry/*"
                />
                <Route
                  element={
                    <OrgTypeGate orgTypes={RECYCLER_TYPES}>
                      <ComingSoonPage />
                    </OrgTypeGate>
                  }
                  path="/recycling-records/*"
                />
                <Route
                  element={
                    <OrgTypeGate orgTypes={REFURBISHER_TYPES}>
                      <ComingSoonPage />
                    </OrgTypeGate>
                  }
                  path="/second-life-passports/*"
                />
                <Route
                  element={
                    <OrgTypeGate orgTypes={["certification_lab"]}>
                      <ComingSoonPage />
                    </OrgTypeGate>
                  }
                  path="/certifications/*"
                />
                <Route
                  element={
                    <OrgTypeGate orgTypes={["regulatory_body"]}>
                      <ComingSoonPage />
                    </OrgTypeGate>
                  }
                  path="/regulatory-access/*"
                />*/}

                <Route element={<IconPage />} path="/icon" />
              </Route>

              {/* Catch-all redirect */}
              <Route element={<Navigate replace to="/" />} path="*" />
            </Routes>
          </Suspense>
        </ErrorBoundary>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
